/**
 * Currency formatting utilities for consistent currency display
 */

/**
 * Formata valor em centavos para moeda brasileira
 * @param cents Valor em centavos
 * @returns String formatada em reais (ex: R$ 150,00)
 */
export const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(cents / 100)
}

/**
 * Formata valor em centavos para moeda brasileira sem o símbolo
 * @param cents Valor em centavos
 * @returns String formatada sem símbolo (ex: 150,00)
 */
export const formatCurrencyValue = (cents: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cents / 100)
}

/**
 * Converte string de moeda para centavos
 * @param currencyString String de moeda (ex: "R$ 150,00" ou "150,00")
 * @returns Valor em centavos
 */
export const parseCurrencyToCents = (currencyString: string): number => {
  // Remove símbolos e espaços, mantém apenas números, vírgula e ponto
  const cleanString = currencyString.replace(/[^\d,.-]/g, '')

  // Converte vírgula para ponto (padrão brasileiro)
  const numberString = cleanString.replace(',', '.')

  // Converte para número e multiplica por 100 para centavos
  const value = parseFloat(numberString) || 0
  return Math.round(value * 100)
}

/**
 * Formata input de moeda em tempo real (para usar em campos de formulário)
 * Trata cada dígito digitado como centavos e formata automaticamente
 *
 * @param value String do input (pode conter qualquer caractere)
 * @returns String formatada em reais (ex: "R$ 150,00")
 *
 * @example
 * formatCurrencyInput("5") // "R$ 0,05"
 * formatCurrencyInput("50") // "R$ 0,50"
 * formatCurrencyInput("500") // "R$ 5,00"
 * formatCurrencyInput("5000") // "R$ 50,00"
 * formatCurrencyInput("R$ 50,00") // "R$ 50,00" (remove formatação anterior)
 */
export const formatCurrencyInput = (value: string): string => {
  // Remove tudo exceto números
  const onlyNumbers = value.replace(/\D/g, '')

  if (onlyNumbers === '') {
    return ''
  }

  // Converte para número (já em centavos) e formata
  const cents = parseInt(onlyNumbers) || 0
  return formatCurrency(cents)
}

/**
 * Extrai o valor em centavos de uma string de input de moeda
 * Usado em conjunto com formatCurrencyInput para obter o valor numérico
 *
 * @param formattedValue String formatada (ex: "R$ 150,00")
 * @returns Valor em centavos
 *
 * @example
 * parseCurrencyInputToCents("R$ 50,00") // 5000
 * parseCurrencyInputToCents("") // 0
 */
export const parseCurrencyInputToCents = (formattedValue: string): number => {
  const onlyNumbers = formattedValue.replace(/\D/g, '')
  return parseInt(onlyNumbers) || 0
}